import { CalendarDays, ChevronDown, ChevronRight, GitBranch, MapPin, UserPen } from "lucide-react";
import { useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DateField } from "@/components/DateField";
import { PlaceFilter } from "@/components/pipeline/PlaceFilter";

const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
const iso = (d) => d.toISOString().slice(0, 10);
const ago = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const PRESETS = [
  ["Aujourd'hui", () => [iso(new Date()), ""]], ["7 derniers jours", () => [ago(7), ""]], ["30 derniers jours", () => [ago(30), ""]],
  ["Ce mois-ci", () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth(), 1, 12)), ""]; }],
  ["Mois dernier", () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth() - 1, 1, 12)), iso(new Date(d.getFullYear(), d.getMonth(), 0, 12))]; }],
];

export const activeFilterCount = (f) => f.pipelines.length + f.step_ids.length + f.creators.length + f.owners.length + (f.date_from || f.date_to ? 1 : 0) + (f.depts?.length || 0) + (f.city ? 1 : 0);

function PipelineTree({ pipelines, filters, set, board }) {
  const [openName, setOpenName] = useState(null);
  return (
    <div className="pl-ftree" data-testid="filter-pipelines-tree">
      {pipelines.map((p) => {
        const expanded = board || openName === p.name;
        return (
          <div key={p.name}>
            <div className="pl-frow">
              {!board && <Checkbox checked={filters.pipelines.includes(p.name)} onCheckedChange={() => set({ pipelines: toggle(filters.pipelines, p.name), pipeline: "" })} data-testid={`filter-pipeline-${p.name}`} />}
              <button type="button" className="pl-fexpand" onClick={() => setOpenName(expanded && !board ? null : p.name)}>{!board && <ChevronRight size={14} className={expanded ? "rot" : ""} />}{p.name}</button>
            </div>
            {expanded && p.steps.map((s) => (
              <label key={s.id} className="pl-frow pl-fstep">
                <Checkbox checked={filters.step_ids.includes(s.id)} onCheckedChange={() => set({ step_ids: toggle(filters.step_ids, s.id), ...(board ? {} : { pipeline: "" }) })} data-testid={`filter-step-${s.id}`} />{s.name}
              </label>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function People({ users, value, onToggle, testid }) {
  const [q, setQ] = useState("");
  const shown = users.filter((u) => u.name?.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <input className="pl-select pl-fsearch" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
      <div className="pl-flist">{shown.map((u) => <label key={u.user_id} className="pl-frow"><Checkbox checked={value.includes(u.user_id)} onCheckedChange={() => onToggle(u.user_id)} data-testid={`${testid}-${u.user_id}`} />{u.name}</label>)}</div>
    </>
  );
}

const Drop = ({ icon: Icon, label, count, testid, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className={`pl-toggle${count ? " on info" : ""}`} data-testid={testid}><Icon size={14} />{label}{count ? ` (${count})` : ""}<ChevronDown size={13} /></button>
    </PopoverTrigger>
    <PopoverContent align="start" className="pl-fpop" data-testid={`${testid}-pop`}>{children}</PopoverContent>
  </Popover>
);

export function FiltersInline({ meta, filters, set, mode, setMode }) {
  const board = mode === "board";
  const pipelines = board && filters.pipeline ? meta.pipelines.filter((p) => p.name === filters.pipeline) : meta.pipelines;
  return (
    <>
      <Drop icon={GitBranch} label={board ? "Étapes" : "Pipelines & étapes"} count={filters.pipelines.length + filters.step_ids.length} testid="filter-pipelines-btn">
        {board && <p className="pl-muted">Cochez les étapes à afficher. <button type="button" className="pl-link" onClick={() => setMode("list")} data-testid="filters-switch-list">Vue Liste</button> pour combiner plusieurs pipelines.</p>}
        <PipelineTree pipelines={pipelines} filters={filters} set={set} board={board} />
      </Drop>
      <Drop icon={CalendarDays} label="Date de création" count={filters.date_from || filters.date_to ? 1 : 0} testid="filter-date-btn">
        <div className="pl-fpresets">{PRESETS.map(([label, fn]) => { const [a, b] = fn(); return <button key={label} type="button" className={`pl-chip${filters.date_from === a && (filters.date_to || "") === b ? "" : " off"}`} onClick={() => set({ date_from: a, date_to: b })} data-testid={`filter-date-${label}`}>{label}</button>; })}</div>
        <div className="pl-fdates">
          <label>Du<DateField className="pl-select" value={filters.date_from || ""} onChange={(v) => set({ date_from: v })} testid="filter-date-from" /></label>
          <label>Au<DateField className="pl-select" value={filters.date_to || ""} onChange={(v) => set({ date_to: v })} testid="filter-date-to" /></label>
        </div>
      </Drop>
      <Drop icon={MapPin} label="Ville / département" count={(filters.depts?.length || 0) + (filters.city ? 1 : 0)} testid="filter-place-btn">
        <PlaceFilter filters={filters} set={set} />
      </Drop>
      <Drop icon={UserPen} label="Créateur" count={filters.creators.length} testid="filter-creator-btn">
        <People users={meta.users} value={filters.creators} onToggle={(id) => set({ creators: toggle(filters.creators, id) })} testid="filter-creator" />
      </Drop>
    </>
  );
}
